import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { api } from '@/lib/api/client'

export type PermissionScope = 'TENANT' | 'REGION' | 'AREA' | 'BRANCH' | 'TEAM' | 'AGENT' | 'SELF'

/** permission code -> scope level. Absent means the role does not hold it. */
export type Grants = Record<string, PermissionScope>

export interface RoleRow {
  roleId: string
  code: string
  displayName: string
  description: string | null
  isSystem: boolean
  grants: Grants
}

export interface PermissionGroup {
  name: string
  permissions: string[]
}

export interface RoleMatrix {
  roles: RoleRow[]
  groups: PermissionGroup[]
  scopes: PermissionScope[]
}

export interface ReplaceResult {
  roleCode: string
  granted: string[]
  revoked: string[]
  rescoped: string[]
}

/**
 * The editor is gated on a tenant flag and the API answers 404 when it is off
 * — deliberately 404 rather than 403, since 403 confirms the endpoint is there
 * and invites a hunt for the switch. `enabled: false` is therefore a normal
 * outcome, not an error, and must not be retried or surfaced as a failure.
 */
export function useRoleMatrix() {
  return useQuery({
    queryKey: ['admin', 'roles', 'matrix'],
    retry: false,
    queryFn: async (): Promise<{ enabled: boolean; matrix: RoleMatrix | null }> => {
      try {
        const res = await api.get<RoleMatrix>('/v1/admin/roles')
        return { enabled: true, matrix: res.data }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return { enabled: false, matrix: null }
        }
        throw err
      }
    },
  })
}

export function useReplaceRolePermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { roleId: string; grants: Grants }) => {
      const res = await api.put<ReplaceResult>(
        `/v1/admin/roles/${vars.roleId}/permissions`,
        { grants: vars.grants },
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles', 'matrix'] })
    },
  })
}
